import type { LineInput, LineType } from '@/pages/approval/useApproval';

/**
 * 결재선의 순서와 갈래 — 기안 화면(카드)과 결재함(기안문 결재란)이 <b>같은 규칙</b>을 쓰도록 한곳에 둔다.
 *
 * 두 화면이 각자 계산하던 때에는 화면마다 참조가 합의 옆에 붙거나 순번이 달라졌다. 결재선의 번호는
 * 엔진이 차례를 도는 <b>seq</b> 그 자체라, 보여 주는 쪽에서 다시 매기면 화면과 실제가 어긋난다.
 */

export type BandKind = 'APPROVAL' | 'AGREEMENT' | 'REFERENCE';

/** 결재선 유형 → 밴드: 결재는 윗줄, 합의(병렬 포함)는 아랫줄, 참조는 따로. */
export function bandKind(lineType: LineType): BandKind {
  if (lineType === 'AGREEMENT' || lineType === 'PARALLEL_AGREEMENT') return 'AGREEMENT';
  if (lineType === 'REFERENCE') return 'REFERENCE';
  return 'APPROVAL';
}

/**
 * 밴드별 <b>원본 자리(index)</b> 목록. 줄 자체를 복사하지 않고 자리만 모으는 이유는,
 * 화면이 어떻게 나뉘든 번호와 수정 대상이 늘 원본 배열을 가리키게 하기 위해서다.
 */
export function groupByBand(lines: LineInput[]): Record<BandKind, number[]> {
  const grouped: Record<BandKind, number[]> = { APPROVAL: [], AGREEMENT: [], REFERENCE: [] };
  lines.forEach((line, index) => grouped[bandKind(line.lineType)].push(index));
  return grouped;
}

/**
 * 같은 밴드 안에서 앞뒤 카드와 자리를 맞바꾼다 — 두 줄의 seq 를 서로 교환하는 것이라
 * 결재선 전체 순서도 함께 바뀐다. 밴드의 끝을 넘어가면 아무것도 하지 않는다.
 */
export function moveWithinBand(lines: LineInput[], indices: number[], posInBand: number, delta: number): LineInput[] {
  const from = indices[posInBand];
  const to = indices[posInBand + delta];
  if (from == null || to == null) return lines;
  const next = [...lines];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

/**
 * 기안문 결재란에 그릴 줄 — <b>참조는 뺀다.</b> 참조자는 결재를 받지 않는 열람자라,
 * 결재란에 칸을 두면 서명을 기다리는 사람처럼 읽힌다.
 */
export function signableLines<T extends { lineType: LineType }>(lines: T[]): T[] {
  return lines.filter((line) => line.lineType !== 'REFERENCE');
}

/**
 * 기안문 머리의 「참 조:」 줄에 이름이 찍히는 참조자 — <b>숨김 참조는 뺀다.</b>
 *
 * 숨김 참조는 참조로서 할 일은 다 한다(수신·참조함에 뜨고 문서를 연다). 다만 <b>문서에 이름이 남지 않는다</b> —
 * 감사·법무처럼 "받았다는 사실 자체를 문서에 적고 싶지 않은" 참조가 그렇다. 기안 화면과 결재함이 이 한 곳을
 * 같이 쓴다 — 한쪽만 걸러 내면 미리보기에는 없던 이름이 결재함에서 되살아난다.
 */
export function visibleReferences<T extends { lineType: LineType; hidden?: boolean }>(lines: T[]): T[] {
  return lines.filter((line) => line.lineType === 'REFERENCE' && !line.hidden);
}

/** 참조 칸의 이름표 하나 — 사람 한 명이거나, [부서 전체]로 들어온 줄들의 묶음. {@code indices} 는 원본 자리. */
export type ReferenceEntry = { label: string; indices: number[]; group: boolean };

/**
 * 참조 줄들을 <b>보이는 단위</b>로 접는다 (사용자 지시 2026-10-02 — 「부서 전체로 지정했는데 한 명
 * 한 명 다 나온다. 부서로만 나오게」).
 *
 * <p>줄은 사람 하나씩이다(참조함·열람권한이 그 단위로 돈다). 다만 [부서 전체]로 들어온 줄에는
 * {@code refGroup} 에 그 부서 이름이 적혀 있어, 같은 이름의 줄을 이름표 하나로 모은다. 묶음의
 * 자리는 그 묶음의 첫 줄이 선 자리다. 묶음 없이 고른 사람은 그대로 한 장씩이다.
 *
 * <p>기안 화면의 참조 칸, 참조자 지정 창의 오른쪽, 기안문의 「참 조:」 줄이 <b>모두 이 함수</b>를
 * 쓴다 — 한 곳만 따로 접으면 창에서는 「공무팀」이었던 것이 종이에서 스무 명으로 되살아난다.
 */
export function referenceEntries<T extends { approverName: string | null; refGroup?: string | null }>(
  lines: T[],
  indices: number[],
): ReferenceEntry[] {
  const entries: ReferenceEntry[] = [];
  const groups = new Map<string, ReferenceEntry>();
  for (const index of indices) {
    const line = lines[index];
    if (!line) continue;
    const group = line.refGroup?.trim();
    if (group) {
      let entry = groups.get(group);
      if (!entry) {
        entry = { label: group, indices: [], group: true };
        groups.set(group, entry);
        entries.push(entry);
      }
      entry.indices.push(index);
    }
    else {
      entries.push({ label: line.approverName ?? '', indices: [index], group: false });
    }
  }
  return entries;
}

/** 기안문 「참 조:」 줄에 찍히는 이름들 — 보이는 참조만, 묶음은 부서 이름 하나로. */
export function referenceLabels<T extends { lineType: LineType; hidden?: boolean; approverName: string | null; refGroup?: string | null }>(
  lines: T[],
): string[] {
  const indices = lines.flatMap((line, index) => (line.lineType === 'REFERENCE' && !line.hidden ? [index] : []));
  return referenceEntries(lines, indices).map((entry) => entry.label).filter(Boolean);
}
